import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const PROFILE_COLS = "id, full_name, email, role, active, mobile_number, home_pod_id";
// Treat expired/invalid JWT as an auth failure we can clear safely.
const isAuthErr = (e) =>
  !!e && (e.code === "PGRST301" || /jwt|token|expired|refresh|401/i.test(`${e.code || ""} ${e.message || ""}`));

export function AuthProvider({ children }) {
  // session: undefined = still initialising, null = signed out, object = signed in
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [authError, setAuthError] = useState(false); // recoverable, unrecoverable-from-code state
  const [bootTick, setBootTick] = useState(0);

  // 1) Session bootstrap. MUST always resolve — a corrupted/expired persisted
  //    session must never leave the app hanging on `undefined` forever.
  useEffect(() => {
    let settled = false;
    const settle = (s) => { if (!settled) { settled = true; setSession(s); } };
    const failSafe = setTimeout(() => settle(null), 8000); // never hang on getSession()

    supabase.auth.getSession()
      .then(({ data }) => settle(data.session ?? null))
      .catch(async () => {
        // Persisted session is unreadable/corrupt — clear it and continue signed out.
        try { await supabase.auth.signOut(); } catch (_) { /* ignore */ }
        settle(null);
      })
      .finally(() => clearTimeout(failSafe));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      settled = true;
      clearTimeout(failSafe);
      setSession(s ?? null);
    });
    return () => { clearTimeout(failSafe); sub.subscription.unsubscribe(); };
  }, [bootTick]);

  const uid = session?.user?.id;

  // 2) Profile load, keyed on user id. MUST always terminate and must recover
  //    from an invalid/revoked session rather than spinning.
  useEffect(() => {
    if (session === undefined) return; // still initialising
    let active = true;
    setAuthError(false);
    if (!uid) { setProfile(null); setProfileLoading(false); return; }
    setProfileLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from("user_profiles").select(PROFILE_COLS).eq("id", uid).maybeSingle();
        if (!active) return;
        if (error) {
          if (isAuthErr(error)) {
            // Expired/invalid token that couldn't refresh — drop it, back to entry.
            try { await supabase.auth.signOut(); } catch (_) { /* ignore */ }
            setProfile(null);
          } else {
            setProfile(null); setAuthError(true); // transient (network) — recoverable
          }
        } else if (!data) {
          // Valid session but no accessible profile (revoked/stale) — recoverable screen.
          setProfile(null); setAuthError(true);
        } else {
          setProfile(data);
        }
      } catch (e) {
        if (active) { setProfile(null); setAuthError(true); }
      } finally {
        if (active) setProfileLoading(false);
      }
    })();
    return () => { active = false; };
  }, [uid, session]);

  const signOut = async () => {
    try { await supabase.auth.signOut(); } catch (_) { /* ignore */ }
    setProfile(null); setSession(null); setAuthError(false);
  };

  // Re-run the whole bootstrap (used by the "Try again" recovery action).
  const retry = useCallback(() => {
    setAuthError(false); setProfileLoading(true); setSession(undefined);
    setBootTick((n) => n + 1);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!uid) return;
    const { data } = await supabase
      .from("user_profiles").select(PROFILE_COLS).eq("id", uid).maybeSingle();
    setProfile(data || null);
  }, [uid]);

  const value = {
    session: session ?? null,
    profile,
    role: profile?.role,
    loading: session === undefined || (!!uid && profileLoading && !authError),
    authError,
    signOut,
    retry,
    refreshProfile,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
